import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  Button,
  message,
  Divider,
  Space,
} from "antd";
import type { RcFile } from "antd/es/upload/interface";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import retrieveCurrencies from "../../redux/actions/currencyActions";
import {
  createDistributor,
  getDistributors,
  resetDistributor,
} from "../../redux/actions/distributorActions";
import retrievePlatforms, {
  createPlatform,
  resetPlatform,
} from "../../redux/actions/platformActions";
import { RootState } from "../../redux/rootReducer";
import { Currency } from "../../types/Currency/Currency";
import { Platform } from "../../types/Platform/Platform";

const { RangePicker } = DatePicker;

function UploadDataForm({ onFileUpload }: { onFileUpload: any }) {
  const dispatch = useDispatch();
  const [platformName, setPlatformName] = useState("");
  const [distributorName, setDistributorName] = useState("");
  const { currencies } = useSelector((state: RootState) => state.currency);
  const { allPlatforms, pending, fulfilled } = useSelector(
    (state: RootState) => state.platform,
  );
  const { allDistributors, distributorPending, distributorFulfilled } =
    useSelector((state: RootState) => state.distributor);

  useEffect(() => {
    dispatch(retrieveCurrencies());
    dispatch(retrievePlatforms({ page: "all" }));
    dispatch(getDistributors({ page: "all" }));
  }, []);

  const beforeUpload = (file: RcFile) => {
    const isMsExcelOrCsv = [
      "text/csv",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ].includes(file.type);
    if (!isMsExcelOrCsv) {
      message.error("You can only upload ms-excel/csv file!");
      return Upload.LIST_IGNORE;
    }
    const isLt2M = file.size / 1024 / 1024 < 200;
    if (!isLt2M) {
      message.error("File must smaller than 200MB!");
      return Upload.LIST_IGNORE;
    }
    onFileUpload(file);
    return false;
  };

  const onRemove = () => {
    onFileUpload(null);
  };

  const setFileInForm = (e: any) => e && e.fileList;

  const onPlatformChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPlatformName(event.target.value);
  };

  const addPlatform = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    // console.log("Platform Added: ", platformName);
    dispatch(createPlatform({ name: platformName }));
  };

  useEffect(() => {
    if (fulfilled) {
      setPlatformName("");
      dispatch(retrievePlatforms({ page: "all" }));
      message.success("Platform added");
      dispatch(resetPlatform());
    }
  }, [pending]);

  const onDistributorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDistributorName(event.target.value);
  };

  const addDistributor = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    // console.log("Distributor Added: ", distributorName);
    dispatch(createDistributor({ name: distributorName }));
  };

  useEffect(() => {
    if (distributorFulfilled) {
      setDistributorName("");
      dispatch(getDistributors({ page: "all" }));
      message.success("Distributor added");
      dispatch(resetDistributor());
    }
  }, [distributorPending]);

  return (
    <div>
      <Form.Item
        name="file_data"
        label="Select Data File"
        rules={[{ required: true }]}
        valuePropName="file"
        getValueFromEvent={setFileInForm}
      >
        <Upload
          accept="text/csv,
      application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
      application/vnd.ms-excel"
          maxCount={1}
          beforeUpload={beforeUpload}
          onRemove={onRemove}
        >
          <Button icon={<UploadOutlined />}>Click to Upload</Button>
        </Upload>
      </Form.Item>
      <div className="grid grid-cols-2 gap-6">
        <Form.Item name="period" label="Period">
          <RangePicker className="w-full" />
        </Form.Item>
        <Form.Item
          name="currency"
          label="Currency Type"
          rules={[{ required: true }]}
        >
          <Select
            placeholder="Please select"
            filterOption={(inputValue, option) =>
              option?.props.children
                .toString()
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
            showSearch
            allowClear
          >
            {currencies.map((item: Currency) => (
              <Select.Option key={item.name} value={item.id}>
                {item.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <Form.Item
          name="distributor"
          label="Distributor"
          rules={[{ required: true }]}
        >
          <Select
            placeholder="Please select"
            filterOption={(inputValue, option) =>
              option?.props.children
                .toString()
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
            showSearch
            allowClear
            // eslint-disable-next-line react/no-unstable-nested-components
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider className="my-2" />
                <Space className="px-2 pb-1">
                  <Input
                    placeholder="Please enter"
                    value={distributorName}
                    onChange={onDistributorChange}
                  />
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={addDistributor}
                  >
                    Add Distributor
                  </Button>
                </Space>
              </>
            )}
          >
            {allDistributors.map((item: any) => (
              <Select.Option key={item.id} value={item.id}>
                {item.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="platform"
          label="Platform"
          rules={[{ required: true }]}
        >
          <Select
            placeholder="Please select"
            filterOption={(inputValue, option) =>
              option?.props.children
                .toString()
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
            showSearch
            allowClear
            // TODO reuse: can make this as seperate component
            // eslint-disable-next-line react/no-unstable-nested-components
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider className="my-2" />
                <Space className="px-2 pb-1">
                  <Input
                    placeholder="Please enter"
                    value={platformName}
                    onChange={onPlatformChange}
                  />
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={addPlatform}
                  >
                    Add Platform
                  </Button>
                </Space>
              </>
            )}
          >
            {allPlatforms.map((item: Platform) => (
              <Select.Option key={item.id} value={item.id}>
                {item.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </div>
      <Form.Item name="comment" label="Comment">
        <Input.TextArea />
      </Form.Item>
    </div>
  );
}

export default UploadDataForm;
